// Combat effects glue: local (predicted) and remote shots, explosions, melee. Raycasts run against
// the WASM world; tracers, impacts, muzzle flashes and sounds are handed to the renderer / audio.

import type { AudioEngine } from "../audio";
import { probe } from "../perf/probe";
import { metaIndex } from "../state/meta";
import type { Renderer } from "../render";
import { tracerEvery, weaponFxProfile } from "../render/weaponFx";
import { rayHitboxes, shotDirections } from "../wasm";
import type { WorldHandle } from "../wasm";
import { SurfaceFlags } from "../wasm/types";
import type { FxEventData, PredictedShot, RayHitData, Vec3Like } from "../wasm/types";
import { nearestPlayerHit } from "./shotTrace";
import type { HitboxRay, ShotTarget } from "./shotTrace";
import { SurfaceTable } from "./surfaces";

/** Longest hitscan range (m). */
export const MAX_SHOT_RANGE = 250;
/** Glass panes one bullet passes at most (the server's bound). */
const MAX_PANES_PER_RAY = 8;
/** Blind range of a flash grenade (m), `EquipmentDef::effect_radius`. */
const FLASH_RANGE = 20;
const SMOKE_RADIUS = 5;
const SMOKE_SECONDS = 14;

type ShotEvent = Extract<FxEventData, { kind: "shot" }>;
type ExplosionEvent = Extract<FxEventData, { kind: "explosion" }>;

/** Audio weapon class from the catalog ("burst_rifle" etc. are resolved by the audio engine). */
export function weaponClassOf(weaponId: string): string {
  return metaIndex.value?.weapons.get(weaponId)?.class ?? "rifle";
}

/** World position of a third-person muzzle: a bit in front of and below the eye, to the right. */
export function muzzleFromEye(eye: Vec3Like, yaw: number, pitch: number): Vec3Like {
  const cp = Math.cos(pitch);
  const fx = -Math.sin(yaw) * cp;
  const fy = Math.sin(pitch);
  const fz = -Math.cos(yaw) * cp;
  const rx = Math.cos(yaw);
  const rz = -Math.sin(yaw);
  return {
    x: eye.x + fx * 0.8 + rx * 0.14,
    y: eye.y + fy * 0.8 - 0.12,
    z: eye.z + fz * 0.8 + rz * 0.14,
  };
}

const NO_TARGETS: readonly ShotTarget[] = [];

const wasmHitboxRay: HitboxRay = (ox, oy, oz, dx, dy, dz, maxDist, t) =>
  rayHitboxes(ox, oy, oz, dx, dy, dz, maxDist, t.pos, t.crouchFrac, t.sliding, t.yaw, t.speed, t.grounded, t.air);

export class FxDirector {
  world: WorldHandle | null = null;
  readonly surfaces = new SurfaceTable();
  /**
   * The players a shot of `shooterId` can hit: as the client displays them right now, or as it
   * displayed them at remote time `atTick` (fractional server tick). Set by the session; without
   * it tracers only know the world.
   */
  targets: ((shooterId: number, atTick?: number) => readonly ShotTarget[]) | null = null;
  /**
   * The local player's bullet passed an intact glass pane (it breaks: the server's rule); set by the
   * session to shatter the pane before the server's `GlassBroken` comes back.
   */
  onLocalPane: ((brush: number, at: Vec3Like, dir: Vec3Like) => void) | null = null;
  /** The server's hit test; replaceable for tests. */
  hitboxRay: HitboxRay = wasmHitboxRay;
  private smokeKey = 1;
  /** Shots of the player's current burst so far, and when the last one went off (for the tracer ratio). */
  private localBurst = 0;
  private lastLocalShotMs = -1e9;
  /** Glass panes the last `rayPastGlass` passed. */
  private readonly panes: RayHitData[] = [];

  constructor(
    private readonly renderer: Renderer,
    private readonly audio: AudioEngine,
  ) {}

  ray(origin: Vec3Like, dx: number, dy: number, dz: number, maxDist: number): RayHitData | undefined {
    return this.world?.raycast(origin.x, origin.y, origin.z, dx, dy, dz, maxDist);
  }

  /** Footstep surface under `pos` (concrete when nothing is hit). */
  surfaceUnder(pos: Vec3Like): string {
    const hit = this.world?.raycast(pos.x, pos.y + 0.3, pos.z, 0, -1, 0, 1.2);
    return hit ? this.surfaces.of(hit) : "concrete";
  }

  /**
   * The local player's predicted shot: tracers to the world hit of every pellet (every n-th shot of
   * a fast burst), impacts, muzzle flash, ejected case, shot sound. `seed` comes from
   * `shotSeed(matchSeed, myId, shot.shotCounter)`; `fireInterval` is the weapon's seconds per shot.
   * `remoteTick` is the remote time the screen showed at the moment of the shot, the time the
   * server rewinds the targets to (docs/NETCODE.md "Lag compensation"): the bullets meet the
   * players as they were then, not as this later frame shows them.
   */
  localShot(
    shot: PredictedShot,
    attachments: string[],
    seed: number,
    hideFlash: boolean,
    shooterId: number,
    fireInterval = 0.1,
    remoteTick?: number,
  ): void {
    const muzzle = this.renderer.viewmodel.muzzleWorldPosition();
    const dirs = shotDirections(shot.weaponId, attachments, shot.yaw, shot.pitch, shot.spreadDeg, seed);
    const profile = weaponFxProfile(weaponClassOf(shot.weaponId));
    const now = performance.now();
    if (now - this.lastLocalShotMs > Math.max(300, fireInterval * 3000)) this.localBurst = 0;
    this.lastLocalShotMs = now;
    const drawTracer = this.localBurst++ % tracerEvery(fireInterval) === 0;
    this.traceVolley(shot.origin, dirs, muzzle, true, shooterId, drawTracer, remoteTick);
    if (!hideFlash && dirs.length >= 3) {
      this.renderer.effects.muzzleFlash(muzzle, { x: dirs[0] ?? 0, y: dirs[1] ?? 0, z: dirs[2] ?? 0 }, true, profile.flash);
    }
    this.renderer.effects.ejectShell(profile.shell, profile.shellDelay);
    this.audio.playShot(weaponClassOf(shot.weaponId), { local: true, suppressed: hideFlash });
  }

  /** A remote player's shot, rebuilt from the snapshot event. */
  remoteShot(ev: ShotEvent, attachments: string[], hideFlash: boolean): void {
    const muzzle = muzzleFromEye(ev.origin, ev.yaw, ev.pitch);
    const dirs = shotDirections(ev.weapon, attachments, ev.yaw, ev.pitch, ev.spreadDeg, ev.seed);
    this.traceVolley(ev.origin, dirs, muzzle, false, ev.shooter);
    if (!hideFlash && dirs.length >= 3) {
      this.renderer.effects.muzzleFlash(muzzle, { x: dirs[0] ?? 0, y: dirs[1] ?? 0, z: dirs[2] ?? 0 }, false, weaponFxProfile(weaponClassOf(ev.weapon)).flash);
    }
    this.audio.playShot(weaponClassOf(ev.weapon), { local: false, pos: muzzle, suppressed: hideFlash, source: ev.shooter });
  }

  /**
   * One tracer per pellet, from the muzzle to where the bullet ends: the first player hitbox
   * before the world hit (with a spark on remote shots; the shooter's own spark comes with the
   * server's `Hit`), else the world hit with its impact effect, else the end of the range. Glass
   * panes do not stop a bullet; the local player's bullets break the panes they pass.
   */
  private traceVolley(
    origin: Vec3Like,
    dirs: Float32Array,
    muzzle: Vec3Like,
    local: boolean,
    shooterId: number,
    drawTracer = true,
    atTick?: number,
  ): void {
    const targets = this.targets?.(shooterId, atTick) ?? NO_TARGETS;
    for (let i = 0; i + 2 < dirs.length; i += 3) {
      const dx = dirs[i] ?? 0;
      const dy = dirs[i + 1] ?? 0;
      const dz = dirs[i + 2] ?? 0;
      const hit = this.rayPastGlass(origin, dx, dy, dz);
      const reach = hit ? hit.dist : MAX_SHOT_RANGE;
      const body =
        targets.length > 0
          ? nearestPlayerHit(origin.x, origin.y, origin.z, dx, dy, dz, reach, targets, this.hitboxRay)
          : null;
      if (local && this.onLocalPane) {
        const stop = body ? body.dist : reach;
        for (const pane of this.panes) {
          if (pane.dist < stop) this.onLocalPane(pane.brush, pane.pos, { x: dx, y: dy, z: dz });
        }
      }
      if (body) {
        if (local && probe) {
          const t = targets.find((x) => x.id === body.id);
          if (t) probe.hitreg.bullet(t, body.dist, body.zone, origin, dx, dy, dz);
        }
        const end = { x: origin.x + dx * body.dist, y: origin.y + dy * body.dist, z: origin.z + dz * body.dist };
        if (drawTracer) this.renderer.effects.tracer(muzzle, end, local);
        if (!local) this.renderer.effects.hitSpark(end);
      } else if (hit) {
        if (drawTracer) this.renderer.effects.tracer(muzzle, hit.pos, local);
        this.renderer.effects.impact(hit.pos, hit.normal, local ? hit.dist : undefined);
      } else {
        const far = { x: origin.x + dx * MAX_SHOT_RANGE, y: origin.y + dy * MAX_SHOT_RANGE, z: origin.z + dz * MAX_SHOT_RANGE };
        if (drawTracer) this.renderer.effects.tracer(muzzle, far, local);
      }
    }
  }

  /**
   * The first world hit along a bullet that stops it (distance from `origin`); the intact glass panes
   * before it are left in `this.panes`.
   */
  private rayPastGlass(origin: Vec3Like, dx: number, dy: number, dz: number): RayHitData | undefined {
    this.panes.length = 0;
    let start = 0;
    for (let n = 0; n < MAX_PANES_PER_RAY; n++) {
      const from = { x: origin.x + dx * start, y: origin.y + dy * start, z: origin.z + dz * start };
      const hit = this.ray(from, dx, dy, dz, MAX_SHOT_RANGE - start);
      if (!hit) return undefined;
      hit.dist += start;
      if ((hit.surface & SurfaceFlags.BREAKABLE) === 0) return hit;
      this.panes.push(hit);
      // A ray that starts inside a pane passes out of it.
      start = hit.dist + 0.01;
    }
    return undefined;
  }

  /**
   * Explosion / grenade effect at the event position. `eye` and the view direction (`fx,fy,fz`,
   * unit) place the local player for the flash intensity.
   */
  explosion(ev: ExplosionEvent, eye: Vec3Like, fx: number, fy: number, fz: number): void {
    const kind = ev.equipment;
    this.renderer.effects.explosion(ev.pos, kind);
    this.audio.playExplosion(kind, ev.pos);
    if (kind === "smoke") {
      this.renderer.effects.smoke(this.smokeKey++, ev.pos, SMOKE_RADIUS, SMOKE_SECONDS);
    } else if (kind === "flash") {
      const intensity = this.flashIntensity(ev.pos, eye, fx, fy, fz);
      if (intensity > 0.02) {
        this.renderer.effects.flash(intensity);
        this.audio.playFlashRing(intensity);
      }
    }
  }

  /** The bomb going off (no equipment event for it). */
  bombExplosion(pos: Vec3Like): void {
    this.renderer.effects.explosion(pos, "bomb");
    this.audio.playExplosion("bomb", pos);
  }

  private flashIntensity(pos: Vec3Like, eye: Vec3Like, fx: number, fy: number, fz: number): number {
    const dx = pos.x - eye.x;
    const dy = pos.y - eye.y;
    const dz = pos.z - eye.z;
    const dist = Math.hypot(dx, dy, dz);
    if (dist >= FLASH_RANGE) return 0;
    if (dist > 0.5) {
      const hit = this.ray(eye, dx / dist, dy / dist, dz / dist, dist);
      if (hit && hit.dist < dist - 0.3) return 0; // a wall is in the way
    }
    const facing = dist > 0.01 ? (fx * dx + fy * dy + fz * dz) / dist : 1;
    const angle = Math.min(1, Math.max(0.15, (facing + 0.35) / 1.35));
    return (1 - dist / FLASH_RANGE) * angle;
  }

  clear(): void {
    this.smokeKey = 1;
    this.surfaces.clear();
  }
}
